import type {
    MutableBranchStep,
    MutableForStep,
    MutableWhileStep,
    MutableWorkflow,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import type { CaseSeedDraft } from '@/workflow-builder/_contract/derivation';
import type { Draft } from '@/workflow-builder/_contract/draft';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';

export function collectCommittedCaseSeedDrafts(mutableWorkflow: MutableWorkflow): CaseSeedDraft[] {
    const seeds: CaseSeedDraft[] = [];

    for (const threadSteps of mutableWorkflow.stepsByThreadIndex) {
        for (const step of threadSteps) {
            switch (step.stepKind) {
                case 'branch': {
                    const branchStep = step as MutableBranchStep;
                    branchStep.cases.forEach((candidate, ownerCaseIndex) => {
                        seeds.push({
                            ownerMacroStepKey: branchStep.stepKey,
                            ownerCaseIndex,
                            slotFamily: 'cases',
                            whenToolStepKey: candidate.when.stepKey,
                            whatToolStepKey: candidate.what.stepKey,
                            placeholderIndexWhen: null,
                            placeholderIndexWhat: null,
                        });
                    });
                    break;
                }
                case 'while':
                case 'for': {
                    const loopStep = step as MutableWhileStep | MutableForStep;
                    seeds.push({
                        ownerMacroStepKey: loopStep.stepKey,
                        ownerCaseIndex: 0,
                        slotFamily: 'case',
                        whenToolStepKey: loopStep.case.when.stepKey,
                        whatToolStepKey: loopStep.case.what.stepKey,
                        placeholderIndexWhen: null,
                        placeholderIndexWhat: null,
                    });
                    break;
                }
                default:
                    break;
            }
        }
    }

    return seeds;
}

export function collectDraftCaseSeedDrafts(draft: Draft | null): CaseSeedDraft[] {
    if (draft === null) {
        return [];
    }

    switch (draft.draftKind) {
        case 'branch': {
            const ownerMacroStepKey = `draft-branch-${draft.caseSetDraft.caseSetKey}`;
            const regularCaseSeeds = draft.caseSetDraft.cases.map((candidate, ownerCaseIndex): CaseSeedDraft => ({
                ownerMacroStepKey,
                ownerCaseIndex,
                slotFamily: 'cases',
                whenToolStepKey: candidate.when?.stepKey ?? null,
                whatToolStepKey: candidate.what?.stepKey ?? null,
                placeholderIndexWhen: candidate.placeholderIndexWhen ?? null,
                placeholderIndexWhat: candidate.placeholderIndexWhat ?? null,
            }));

            const defaultCaseSeed = draft.caseSetDraft.defaultCase
                ? [{
                    ownerMacroStepKey,
                    ownerCaseIndex: draft.caseSetDraft.cases.length,
                    slotFamily: 'cases' as const,
                    whenToolStepKey: draft.caseSetDraft.defaultCase.when?.stepKey ?? null,
                    whatToolStepKey: draft.caseSetDraft.defaultCase.what?.stepKey ?? null,
                    placeholderIndexWhen: draft.caseSetDraft.defaultCase.placeholderIndexWhen ?? null,
                    placeholderIndexWhat: draft.caseSetDraft.defaultCase.placeholderIndexWhat ?? null,
                }]
                : [];

            return [...regularCaseSeeds, ...defaultCaseSeed];
        }
        case 'while':
        case 'for': {
            return [{
                ownerMacroStepKey: `draft-${draft.draftKind}-${draft.caseDraft.caseKey}`,
                ownerCaseIndex: 0,
                slotFamily: 'case',
                whenToolStepKey: draft.caseDraft.when?.stepKey ?? null,
                whatToolStepKey: draft.caseDraft.what?.stepKey ?? null,
                placeholderIndexWhen: draft.caseDraft.placeholderIndexWhen ?? null,
                placeholderIndexWhat: draft.caseDraft.placeholderIndexWhat ?? null,
            }];
        }
        case 'tool':
            return [];
    }
}

export function collectCaseSeedDrafts(runtimeState: RuntimeState): CaseSeedDraft[] {
    return [
        ...collectCommittedCaseSeedDrafts(runtimeState.mutableWorkflow),
        ...collectDraftCaseSeedDrafts(runtimeState.draft),
    ];
}